import z, { prettifyError, ZodError } from 'zod'
import {
    AccreditationConfigStateDoc,
    AccreditationDocumentConfig,
    accreditationDocumentConfigSchema,
    AccreditationDocumentsForSetting,
} from './types-accreditation'
import { EmploymentSetting } from './types-teacher'
import { get, ref } from 'firebase/database'
import { db } from './configs'
import { captureException } from '@sentry/react'
import { logger } from '../helpers/logger'

// prettier-ignore
export const ausStates = z.enum(['VIC', 'NSW', 'TAS', 'QLD', 'SA', 'NT', 'ACT', 'WA'] as const)
export type AusState = z.infer<typeof ausStates>

interface IAccreditationConfig {
    setting: EmploymentSetting
}

export const getAccreditationConfig = async (
    props: IAccreditationConfig
): Promise<Record<string, AccreditationDocumentConfig>> => {
    try {
        if (!isSettingActiveInArea(props.setting)) return {}

        // logger.debug('fetching config...')

        // Get list from states nodes
        const docKeys = Object.entries(await getStateTypeList(props))

        const configRes = await Promise.allSettled(
            docKeys.map(([key, stateConfig]) =>
                getAccreditationDocConfig(key, stateConfig)
            )
        )

        // logger.debug(configRes)

        return docKeys.reduce(
            (a, [type], i) => {
                try {
                    const r = configRes[i]
                    if (r.status === 'fulfilled') {
                        if (r.value === null) {
                            throw new Error('document config was not found')
                        }
                        a[type] = {
                            ...r.value,
                            // NOTE: Spread out type specific field overrides
                            ...(r.value.by_employment_type?.[
                                props.setting.type
                            ] || {}),
                        }
                    } else {
                        throw r.reason instanceof Error
                            ? r.reason
                            : new Error(String(r.reason))
                    }
                } catch (e) {
                    const reason =
                        e instanceof ZodError
                            ? `schema validation failed: ${prettifyError(e)}`
                            : e instanceof Error
                              ? e.message
                              : String(e)
                    logger.error(
                        `Missing accreditation config for "${type}": ${reason}`
                    )
                    captureException('Missing accreditation config', {
                        tags: {
                            accreditationType: type,
                        },
                        extra: {
                            reason,
                        },
                    })
                } finally {
                    return a
                }
            },
            {} as Record<string, AccreditationDocumentConfig>
        )
    } catch (e) {
        logger.error(e)
        throw e
    }
}

const getStateTypeList = async (
    props: IAccreditationConfig
): Promise<AccreditationDocumentsForSetting> => {
    const { v2 } = typeToKey(props.setting)
    const stateListSnap = await get(
        ref(
            db,
            `accreditationConfig/states/${props.setting.locationState}/${v2}`
        )
    )
    if (!stateListSnap.exists()) return {}
    return stateListSnap.val()

    // return Object.keys(docsForType)
}

const getAccreditationDocConfig = async (
    key: string,
    stateConfig: AccreditationConfigStateDoc
): Promise<AccreditationDocumentConfig | null> => {
    const snap = await get(ref(db, `accreditationConfig/documents/${key}`))
    if (!snap.exists()) return null
    return accreditationDocumentConfigSchema.parse({
        ...snap.val(),
        primary: stateConfig.primary,
        optional: stateConfig.optional,
        short_title: stateConfig.title,
        type: key,
    })
}

const typeToKey = (
    setting: EmploymentSetting
): { v2: string; defaultKey: string } => {
    switch (setting.type) {
        case 'school':
            return { v2: 'school_docs', defaultKey: 'school_docs' }
        case 'earlyChildcare': {
            return {
                v2: setting.level ? `ecc_docs_${setting.level}` : 'ecc_docs',
                defaultKey: `ecc_docs`,
            }
        }
        case 'supportStaff': {
            return {
                v2:
                    typeof setting.hasQualification === 'boolean'
                        ? `supportStaff_docs_${setting.hasQualification ? 'hasQualification' : 'noQualification'}`
                        : 'supportStaff_docs',
                defaultKey: 'supportStaff_docs',
            }
        }
    }
}

const isSettingActiveInArea = (setting: EmploymentSetting): boolean => {
    if (
        !setting?.locationState ||
        !ausStates.default('VIC').safeParse(setting.locationState).success
    ) {
        return false
    }

    if (setting.type === 'earlyChildcare') {
        return Boolean(setting.level)
    }

    return true
}
